import {
  Ban,
  CalendarCheck,
  HelpCircle,
  ListFilter,
  LogOut,
  PlusCircle,
  Receipt,
  Star,
  Trash2,
  Wallet,
} from "lucide-react";
import { hasAvatarImage } from "../../utils/avatar";
import { formatJoinMonthYear, getUserJoinDate } from "../../helpers/date";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation, useNavigate } from "react-router";
import { useDeleteAccount, useGetUserProfile } from "../../hooks/useUser";
import { useGetProfileFeedbacks } from "../../hooks/useFeedback";
import { useGetMyDashboardPropertiesWithFilters } from "../../hooks/useDashboard";
import { useOpenSupportChat } from "../../chat/official/support";
import { useAuthStore } from "../../store/useAuthStore";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { AccountTypeBadge } from "../components/AccountTypeBadge";

export const AccountLayout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const logout = useAuthStore((s) => s.logout);
  const deleteAccountMutation = useDeleteAccount();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const supportChat = useOpenSupportChat();

  const userId = useAuthStore((s) => s.id) || 0;

  const { data: user } = useGetUserProfile(userId);
  // users.property_count is a stored counter that goes stale (it kept «1» after
  // the last listing was deleted) — count what /dashboard actually lists.
  const { data: myListings } = useGetMyDashboardPropertiesWithFilters(0, 1);
  const myListingsCount = Number(myListings?.total ?? myListings?.list?.length ?? 0) || undefined;
  // Number of reviews next to the stars — it used to print the rating there
  // («5.00 пикир»), which read as five reviews.
  const { data: feedbackData } = useGetProfileFeedbacks(userId);
  const reviewCount: number = feedbackData?.total ?? feedbackData?.list?.length ?? 0;
  const ratingValue = Number(user?.rating) || 0;

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const isAvatarUrl = hasAvatarImage(user?.avatar);
  const initials = user?.name?.charAt(0).toUpperCase() || "U";

  const roleLabelOptions: Record<string, string> = {
    COMPANY: t("filters.ownerType.company", "Компания"),
    REALTOR: t("filters.ownerType.realtor", "Риелтор"),
    AGENCY: t("filters.ownerType.agency", "Агентство"),
    OWNER: t("filters.ownerType.private", "Собственник"),
  };
  const roleLabel = roleLabelOptions[user?.type ?? ""] || "-";

  const navItems = [
    {
      key: "create-listing",
      icon: PlusCircle,
      label: t("nav.post", "Кошуу жарыя"),
      path: "/create-listing",
    },
    {
      key: "listings",
      icon: ListFilter,
      label: t("profile.nav.listings", "Менин жарнамаларым"),
      badge: myListingsCount,
      path: "/dashboard",
    },
    // {
    //   key: "balance",
    //   icon: Wallet,
    //   label: t("profile.balance.title", "Баланс"),
    //   path: "/balance/top-up",
    // },
    // {
    //   key: "transactions",
    //   icon: Receipt,
    //   label: t("profile.balance.history", "Транзакциялар"),
    //   path: "/balance/transactions",
    // },
    {
      key: "requests",
      icon: CalendarCheck,
      label: t("nav.myRequests", "Мои заявки"),
      path: "/bookings",
    },
    {
      key: "profile",
      icon: Star,
      label: t("profile.nav.settings", "Профиль жөндөөлөрү"),
      path: "/profile",
    },
    {
      key: "blocked",
      icon: Ban,
      label: t("block.title", "Заблокированные"),
      path: "/profile/blocked",
    },
  ];

  const isActive = (path: string) =>
    path === "/profile"
      ? location.pathname === "/profile"
      : location.pathname.startsWith(path);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="lg:container lg:mx-auto lg:px-4 lg:py-6 lg:flex lg:gap-6">
        {/* ══ SIDEBAR — только десктоп ══ */}
        <aside className="hidden lg:flex flex-col gap-4 w-64 shrink-0">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="h-20 bg-gradient-to-r from-blue-500 to-indigo-600 relative">
              <div className="absolute -bottom-10 left-1/2 -translate-x-1/2">
                <div className="relative">
                  {isAvatarUrl ? (
                    <img
                      src={user?.avatar}
                      alt={user?.name}
                      className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-lg"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full border-4 border-white shadow-lg bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center">
                      <span className="text-3xl font-extrabold text-white">
                        {initials}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="pt-12 pb-5 px-4 text-center">
              <p className="font-bold text-gray-900 text-base">
                {user?.name || "—"}
              </p>
              {user?.type ? (
                <div className="mt-1.5 flex justify-center">
                  <AccountTypeBadge type={user.type} verified={Boolean((user as { is_tick?: boolean }).is_tick)} size="sm" />
                </div>
              ) : (
                <p className="text-blue-600 text-xs font-semibold mt-0.5">{roleLabel}</p>
              )}
              <div className="flex items-center justify-center gap-1 mt-2">
                {[1, 2, 3, 4, 5].map((s) => (
                  <Star
                    key={s}
                    size={13}
                    className={
                      s <= Math.round(ratingValue)
                        ? "fill-amber-400 text-amber-400"
                        : "text-gray-200 fill-gray-200"
                    }
                  />
                ))}
                <span className="text-xs text-gray-500 ml-1">
                  {reviewCount > 0
                    ? `${ratingValue.toFixed(1)} (${reviewCount})`
                    : t("profile.noReviews", "Пока нет отзывов")}
                </span>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                {t("profile.memberSince", "На сайте с")}{" "}
                {formatJoinMonthYear(getUserJoinDate(user)) || "—"}
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <nav className="py-2">
              {navItems.map(({ key, icon: Icon, label, badge, path }) => {
                const active = isActive(path);
                return (
                  <button
                    key={key}
                    onClick={() => navigate(path)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-all ${
                      active
                        ? "bg-blue-50 text-blue-600 border-r-2 border-blue-600"
                        : "text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    <Icon
                      size={16}
                      className={active ? "text-blue-600" : "text-gray-400"}
                    />
                    <span className="flex-1 text-sm font-medium">{label}</span>
                    {badge !== undefined && badge !== null && (
                      <span
                        className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${
                          active ? "bg-blue-100 text-blue-600" : "text-blue-500"
                        }`}
                      >
                        {badge}
                      </span>
                    )}
                  </button>
                );
              })}
              <div className="border-t border-gray-100 mt-1 pt-1">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-red-500 hover:bg-red-50 transition"
                >
                  <LogOut size={16} />
                  <span className="text-sm font-medium">
                    {t("profile.nav.logout", "Выйти")}
                  </span>
                </button>
                <button
                  onClick={() => setDeleteConfirmOpen(true)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-red-500 hover:bg-red-50 transition"
                >
                  <Trash2 size={16} />
                  <span className="text-sm font-medium">
                    {t("profile.deleteAccount.cta", "Удалить аккаунт")}
                  </span>
                </button>
              </div>
            </nav>
          </div>

          <div
            className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 cursor-pointer"
            onClick={supportChat.open}
          >
            <p className="font-bold text-gray-900 text-sm mb-1">
              {t("profile.needHelp", "Нужна помощь?")}
            </p>
            <p className="text-xs text-gray-400 mb-3">
              {t(
                "profile.helpSub",
                "Мы всегда готовы помочь вам с любыми вопросами",
              )}
            </p>
            <button type="button" disabled={supportChat.pending} className="w-full flex items-center justify-center gap-2 bg-blue-50 hover:bg-blue-100 text-blue-600 text-sm font-semibold py-2.5 rounded-xl transition">
              <HelpCircle size={15} />
              {t("profile.contactSupport", "Связаться с поддержкой")}
            </button>
          </div>
        </aside>

        {/* ══ CONTENT — рендерится дочерним роутом ══ */}
        <main className="flex-1 min-w-0 flex flex-col gap-4">
          <Outlet />
        </main>
      </div>

      <ConfirmDialog
        open={deleteConfirmOpen}
        title={t("profile.deleteAccount.title", "Удалить аккаунт?")}
        description={t(
          "profile.deleteAccount.warning",
          "Это действие необратимо. Все ваши объявления, избранное и данные профиля будут удалены навсегда.",
        )}
        confirmText={t("profile.deleteAccount.confirm", "Удалить")}
        cancelText={t("profile.deleteAccount.cancel", "Отмена")}
        danger
        loading={deleteAccountMutation.isPending}
        onConfirm={() => deleteAccountMutation.mutate()}
        onClose={() => setDeleteConfirmOpen(false)}
      />
    </div>
  );
};
